import {
  Suspense,
  useContext,
  useReducer,
  createContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react"
import { Link } from "react-router-dom"
import { node, string } from "prop-types"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import {
  AlertMessage,
  Loading,
  BreadcrumbNavigationProvider,
  BreadcrumbNavigationPortal,
} from "source/shared/components"
import {
  ProtectAgainstChangingSessions,
  useSession,
} from "source/shared/hooks/useSession"
import {
  BringYourOwnPropsHeader,
  useNotificationCount,
  useChatHeaderProps,
} from "@planningcenter/cc-nav"
import WindowDimensionsProvider from "source/shared/contexts/WindowDimensionsProvider"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import {
  JoltProvider,
  useChatChannel,
  useNotificationsChannel,
} from "source/shared/contexts/JoltContext"
import { QRCode, QRCodeLink, QRCodeProvider } from "source/shared/QRCode"
import { WebBootContext } from "source/publishing/WebBoot"
import { DraftModeBanner } from "source/publishing/DraftMode"
import { ReactToPublishingJoltEvents } from "source/publishing/ReactToPublishingJoltEvents"
import { ChurchCenterIframeProvider } from "source/shared/contexts/ChurchCenterIframeProvider"
import { TitleTemplate } from "source/TitleTemplate"
import { extractMenuItems } from "source/shared/extractMenuItems"
import { useLocalStorage } from "source/shared/hooks/useLocalStorage"
import useQueryString from "source/shared/hooks/useQueryString"
import classNames from "classnames"
import { useEmbedded } from "./shared/hooks/useEmbedded"
import { HelmetProvider } from "react-helmet-async"
import OpenGraphTags from "./shared/OpenGraphTags"
import { ChurchCenterIframeCloseButton } from "./shared/contexts/ChurchCenterIframeProvider"

Layout.propTypes = {
  children: node.isRequired,
}

export const LayoutContext = createContext()

export function isChurchCenterApp() {
  return location.search.includes("source=cca") || window.ReactNativeWebView
}

export function isNativeChurchCenterApp() {
  return location.search.includes("source=cca") && !window.ReactNativeWebView
}

// Church Center App uses the window to communicate the color scheme to Church Center Web rendered inside
// of web views. The app *MUST* communicate the theme to the web view. We make no inferences.
// The agreed upon contract is `forceTheme`, and `setForceTheme`.
//
// window.forceTheme = ''
// window.setForceTheme('')
//
// Church Center Web, on the other hand, follows the system's prefered color scheme.
function useColorScheme() {
  const PREFERS_DARK = "(prefers-color-scheme: dark)"
  const features = useContext(WebBootContext).features
  const [queryParams] = useQueryString({
    theme: null,
  })
  const [storedTheme, setStoredTheme] = useLocalStorage(
    "ChurchCenterTheme",
    null,
  )
  let followSystemTheme
  let defaultColorScheme

  if (window.ReactNativeWebView) {
    followSystemTheme = false
    defaultColorScheme = window.forceTheme || "light"
  } else if (features.ROLLOUT_dark_mode && storedTheme) {
    followSystemTheme = false
    defaultColorScheme = storedTheme || "light"
  } else {
    followSystemTheme = false
    defaultColorScheme = "light"
  }

  const [chosenColorScheme, setChosenColorScheme] = useState(defaultColorScheme)

  // Put queryParam-defined theme in local storage
  useEffect(() => {
    if (queryParams.theme !== null) setStoredTheme(queryParams.theme)
  }, [queryParams.theme])

  // Make setForceTheme available to React Native
  useEffect(() => {
    if (window.ReactNativeWebView) window.setForceTheme = setChosenColorScheme
  }, [!!window.ReactNativeWebView, setChosenColorScheme])

  // Actually follow the system theme changing.
  // Not attached while we roll-out dark mode behind a query param.
  useEffect(() => {
    if (!followSystemTheme) return
    const media = window.matchMedia(PREFERS_DARK)
    const handleChange = (event) =>
      setChosenColorScheme(event.matches ? "dark" : "light")
    media.addEventListener("change", handleChange)
    return () => media.removeEventListener("change", handleChange)
  }, [followSystemTheme, setChosenColorScheme])

  const colorScheme = useMemo(() => {
    switch (chosenColorScheme) {
      case "dark-low":
      case "dark":
        return "dark"
      default:
        return "light"
    }
  }, [chosenColorScheme])

  // Apply the chosen scheme to the document body.
  useEffect(() => {
    document.body.setAttribute("data-color-scheme", colorScheme)
    document.body.setAttribute("data-contrast", "low")
    document.body.setAttribute("data-background-color", "")
  }, [colorScheme])

  // Update the theme based on local storage theme
  useEffect(() => {
    setChosenColorScheme(storedTheme)
  }, [storedTheme])

  return colorScheme
}

export function Layout({ children }) {
  const { flash } = useContext(ServerRenderedProps)
  const { error } = flash
  const [state, dispatch] = useLayoutReducer()
  const embedded = useEmbedded()
  const theme = useColorScheme()

  return (
    <LayoutContext.Provider value={[state, dispatch, theme]}>
      <ChurchCenterIframeProvider>
        <JoltProvider>
          <ReactToPublishingJoltEvents />
          <QRCodeProvider>
            <WindowDimensionsProvider>
              <BreadcrumbNavigationProvider>
                <HelmetProvider>
                  <OpenGraphTags />
                  <TitleTemplate />
                  <PageContainer layoutWidth={state.layoutWidth}>
                    <ChurchCenterIframeCloseButton />
                    <DraftModeBanner />
                    {!embedded && !isChurchCenterApp() && <Header />}
                    {!isChurchCenterApp() && <BreadcrumbNavigationPortal />}

                    <EmbedHeightReportingWrapper
                      id="main_page_content"
                      role="main"
                      className={classNames("f-1", {
                        "page-content": !embedded,
                        "pb-0": !state.footerVisible,
                      })}
                    >
                      <div
                        className={`f-1 container container--${state.layoutWidth}`}
                      >
                        {error && (
                          <AlertMessage className="mb-3">{error}</AlertMessage>
                        )}
                        {embedded ? (
                          <Suspense fallback={<Loading />}>{children}</Suspense>
                        ) : (
                          <ProtectAgainstChangingSessions>
                            <Suspense fallback={<Loading />}>
                              {children}
                            </Suspense>
                          </ProtectAgainstChangingSessions>
                        )}
                      </div>
                    </EmbedHeightReportingWrapper>
                    {!isChurchCenterApp() && <QRCode />}
                    {!embedded && !isChurchCenterApp() && <Footer />}
                  </PageContainer>
                </HelmetProvider>
              </BreadcrumbNavigationProvider>
            </WindowDimensionsProvider>
          </QRCodeProvider>
        </JoltProvider>
      </ChurchCenterIframeProvider>
    </LayoutContext.Provider>
  )
}

export function mapReachMenuLinkProps({ href, ...rest }) {
  if (href.startsWith(window.origin)) href = href.replace(window.origin, "")
  const isChurchCenterHref = href.startsWith("/")
  const key = href
  if (isChurchCenterHref)
    return {
      "aria-current": href === window.location.pathname,
      as: Link,
      key,
      to: href,
      ...rest,
    }
  else
    return {
      as: "a",
      key,
      href,
      target: "_blank",
      rel: "noopener noreferrer",
      ...rest,
    }
}

function PageContainer({ layoutWidth = "", children }) {
  return (
    <div className={`page-container d-f fd-c ${layoutWidth}`}>{children}</div>
  )
}
PageContainer.propTypes = {
  layoutWidth: string,
  children: node,
}

function Header() {
  const [visible, setVisible] = useLayoutHeaderVisible()
  useEffect(() => {
    if (visible === "initialRender") {
      const timeout = window.setTimeout(() => setVisible(true), 100)
      return () => window.clearTimeout(timeout)
    }
  }, [visible])

  const opacity = visible === true ? "1.0" : "0.0"
  return (
    <div
      css={{
        opacity,
        transition: "opacity 0.4s ease",
        [`@media screen and (max-width: ${BREAKPOINTS.LG - 1}px)`]: {
          ".sermon-episode-layout &": {
            display: "none",
          },
        },
      }}
    >
      <BringYourOwnPropsHeader
        {...useHeaderPropsBuiltFromPublishingWebBoot()}
      />
    </div>
  )
}

function Footer() {
  const [visible] = useLayoutFooterVisible()
  const { layout } = useContext(ServerRenderedProps)
  const {
    organization_contact_email: email,
    organization_contact_phone: phone,
    organization_name: name,
  } = layout

  if (!visible) return null

  return (
    <footer className="site-footer" role="contentinfo">
      <ul className="site-footer__links o-2 o-1@sm">
        <li className="site-footer__link">
          <a
            className="c-brand"
            href="https://planning.center/terms/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>
        </li>
        <li className="site-footer__link">
          <a
            className="c-brand"
            href="https://planning.center/privacy/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
        </li>
      </ul>

      <ul className="site-footer__links o-1 o-2@sm">
        <li className="site-footer__link">{name}</li>
        {email && (
          <li className="site-footer__link">
            <a className="fw-400 c-tint2" href={`mailto:${email}`}>
              {email}
            </a>
          </li>
        )}

        {phone && (
          <li className="site-footer__link">
            <a className="fw-400 c-tint2" href={`tel:${phone}`}>
              {phone}
            </a>
          </li>
        )}
      </ul>
      <QRCodeLink />
    </footer>
  )
}

function useHeaderPropsBuiltFromPublishingWebBoot() {
  const { currentOrganization, draftMenu, draftMode, publishedMenu } =
    useContext(WebBootContext)
  const {
    data: currentPerson,
    meta: { authenticated: personIdentified },
  } = useSession(false)
  const notificationsChannel = useNotificationsChannel()
  const chatChannel = useChatChannel()

  const menu = draftMode
    ? extractMenuItems(draftMenu)
    : extractMenuItems(publishedMenu)
  const organizationDisplayName = currentOrganization.attributes.name
  const organizationImageUrl = currentOrganization.attributes.avatar_url
  const personDisplayName = currentPerson.attributes.first_name
  const personImageUrl = currentPerson.attributes.avatar_url + "?g=120x120%23"

  // NOTE: This isn't truly reactive... but it seems to work 'good enough' for today.
  const notificationDelay = window.location.pathname.startsWith(
    "/notifications",
  )
    ? 0
    : 1000

  const notificationCount = useNotificationCount({
    personIdentified,
    notificationsChannel,
    notificationDelay,
  })

  const { chatUnread, pcoChatEnabled } = useChatHeaderProps({
    personIdentified,
    personId: currentPerson.id,
    channel: chatChannel,
    delay: 1000,
  })

  const automaticallyAppendedLinks = ["/profile", "/logout", "/login"]
  const links = menu
    .filter(({ href }) => !automaticallyAppendedLinks.includes(href))
    .map(mapReachMenuLinkProps)

  const returnUrl = new URLSearchParams(window.location.search).get("return")
  const loginLink = mapReachMenuLinkProps({
    href:
      window.location.pathname === "/login" && !returnUrl
        ? window.location.href
        : `/login?return=${returnUrl || window.location.href}`,
    children: "Log in",
  })

  const organizationLink = mapReachMenuLinkProps({
    href: "/home",
    children: "Home",
  })

  const notificationLink = mapReachMenuLinkProps({
    href: "/notifications",
  })

  const myChurchCenterLink = mapReachMenuLinkProps({ href: "/me" })

  return {
    links,
    loginLink,
    myChurchCenterLink,
    notificationCount,
    notificationLink,
    organizationDisplayName,
    organizationImageUrl,
    organizationLink,
    pcoChatEnabled,
    personDisplayName,
    personIdentified,
    personImageUrl,
    chatUnread,
  }
}

function EmbedHeightReportingWrapper({ children, ...props }) {
  const wrapper = useRef()
  const embedded = useEmbedded()

  useEffect(() => {
    if (!embedded || !wrapper.current || !window.ResizeObserver) return

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.parent.postMessage(
          { pageHeight: entry.target.scrollHeight },
          "*",
        )
      })
    })

    observer.observe(wrapper.current)
    return () => observer.disconnect()
  }, [embedded])

  return (
    <div ref={wrapper} {...props}>
      {children}
    </div>
  )
}
EmbedHeightReportingWrapper.propTypes = {
  children: node,
}

export function useLayoutReducer(
  initialLayoutState = {
    layoutWidth: "narrow",
    footerVisible: true,
    headerVisible: "initialRender",
    embedded: false,
  },
) {
  function reducer(state = initialLayoutState, action) {
    switch (action.type) {
      case "setLayoutWidth":
        return { ...state, layoutWidth: action.payload }
      case "showFooter":
        return { ...state, footerVisible: true }
      case "hideFooter":
        return { ...state, footerVisible: false }
      case "showHeader":
        return { ...state, headerVisible: true }
      case "hideHeader":
        return { ...state, headerVisible: false }
      default:
        return state
    }
  }

  return useReducer(reducer, initialLayoutState)
}

export function useLayoutTheme() {
  const context = useContext(LayoutContext)
  if (!context) {
    // allow components to render even if Layout isn't available (e.g. portals)
    console.warn(
      "useLayoutTheme called outside Layout provider, defaulting to 'light'",
    )
    return "light"
  }
  const [, , theme] = context
  return theme
}

export const useLayoutWidth = () => {
  const [state, dispatch] = useContext(LayoutContext)

  return [
    state.layoutWidth,
    (layoutWidth) => {
      dispatch({
        type: "setLayoutWidth",
        payload: layoutWidth,
      })
    },
  ]
}

export const useLayoutHeaderVisible = () => {
  const [state, dispatch] = useContext(LayoutContext)
  return [
    state.headerVisible,
    (visible) => {
      dispatch({ type: visible ? "showHeader" : "hideHeader" })
    },
  ]
}

export const useLayoutFooterVisible = () => {
  const [state, dispatch] = useContext(LayoutContext)
  return [
    state.footerVisible,
    (visible) => {
      dispatch({ type: visible ? "showFooter" : "hideFooter" })
    },
  ]
}

export const useHiddenHeader = () => {
  const [_visible, setVisible] = useLayoutHeaderVisible()
  useEffect(() => {
    setVisible(false)
    return () => setVisible(true)
  }, [])
}

export const FullscreenLayout = ({ children }) => {
  const [currentLayoutWidth, setLayoutWidth] = useLayoutWidth()

  useEffect(() => {
    setLayoutWidth("fullscreen")
    return () => {
      setLayoutWidth(currentLayoutWidth)
    }
  }, [])

  return <>{children}</>
}

FullscreenLayout.propTypes = {
  children: node.isRequired,
}

export const WideLayout = ({ children }) => {
  const [currentLayoutWidth, setLayoutWidth] = useLayoutWidth()

  useEffect(() => {
    setLayoutWidth("wide")
    return () => {
      setLayoutWidth(currentLayoutWidth)
    }
  }, [])

  return <>{children}</>
}

WideLayout.propTypes = {
  children: node.isRequired,
}

export const SermonEpisodeLayout = ({ children }) => {
  const [currentLayoutWidth, setLayoutWidth] = useLayoutWidth()

  useEffect(() => {
    setLayoutWidth("sermon-episode-layout")
    return () => {
      setLayoutWidth(currentLayoutWidth)
    }
  }, [])

  return <>{children}</>
}

SermonEpisodeLayout.propTypes = {
  children: node.isRequired,
}

export const NarrowLayout = ({ children }) => {
  const [currentLayoutWidth, setLayoutWidth] = useLayoutWidth()

  useEffect(() => {
    setLayoutWidth("narrow")
    return () => {
      setLayoutWidth(currentLayoutWidth)
    }
  }, [])

  return <>{children}</>
}

NarrowLayout.propTypes = {
  children: node.isRequired,
}
